<template>
  <div class="info-page">
    <main>
      <section>
        <h2 id="quickstart">Quickstart: Der Grapheditor</h2>
        <p>
          Mit dem Grapheditor erstellen Sie Datenlandkarten: Graphen, die
          durchgeführte Arbeiten dokumentieren, die Kommunikation und den
          Datenaustausch zwischen den beteiligten Stakeholdern (z.B. Kommune,
          Energieversorger, Dienstleister) nachvollziehbar machen, rechtliche
          und vertragliche Rahmenbedingungen abbilden und die Planung von
          Energieeffizienzmaßnahmen unterstützen. Dazu werden Akteure,
          Datenquellen, technische Anlagen und rechtliche Grundlagen als Knoten
          modelliert und über Verbindungen (optional mit Zeitaufwand und
          Kosten) in Beziehung gesetzt. Im Folgenden werden in der
          Kurzbeschreibung zunächst die wichtigsten Schritte zum Erstellen
          eines Graphen beschrieben. Für speziellere Funktionen
          (z.B. Markdown-Beschreibungen, Zeitplanung oder Import/Export) siehe
          „2. Detailliertere Beschreibung“.
        </p>

        <h3>1. Kurzbeschreibung (erster Graph in 5 Schritten)</h3>
        <ul>
          <li>
            <strong>Edit Mode aktivieren:</strong> Über das Schloss-Symbol unten
            rechts wechseln Sie zwischen View Mode 🔒 (schreibgeschützt) und
            Edit Mode 🔓 (Bearbeitung). Zum Erstellen eines Graphen muss der
            Edit Mode aktiv sein.
          </li>
          <li>
            <strong>Knoten anlegen:</strong> Öffnen Sie das Vorlagen-Panel
            (Button „📚 Vorlagen“, falls geschlossen) und ziehen Sie
            Vorlagen-Knoten per Drag &amp; Drop in den Graph-Bereich.
          </li>
          <li>
            <strong>Verbindungen erstellen:</strong> Button „🔗 Verbindung“
            aktivieren, dann Startknoten und anschließend Zielknoten anklicken.
            Alternativ: Rechtsklick auf einen Knoten → „Verbindung erstellen“.
          </li>
          <li>
            <strong>Eigenschaften bearbeiten:</strong> Ein Klick auf einen
            Knoten oder eine Verbindung öffnet das Eigenschaften-Panel rechts.
            Dort lassen sich Titel, Beschreibung und weitere Angaben pflegen.
          </li>
          <li>
            <strong>Speichern:</strong> Der Graph wird automatisch alle
            30 Sekunden lokal im Browser gespeichert. Über „💾 Export“ können
            Sie ihn zusätzlich als JSON-Datei sichern.
          </li>
        </ul>

        <h3>2. Detailliertere Beschreibung</h3>

        <h4>2.1 Knotentypen</h4>
        <p>
          Das Vorlagen-Panel enthält vordefinierte Knotentypen für die
          verschiedenen Bestandteile einer Datenlandkarte:
        </p>
        <div class="table-card">
          <table>
            <thead>
              <tr>
                <th>Typ</th>
                <th>Form</th>
                <th>Farbe</th>
                <th>Verwendung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Start</td>
                <td>Ellipse (klein)</td>
                <td>Rot</td>
                <td>Startpunkt des Prozesses</td>
              </tr>
              <tr>
                <td>End</td>
                <td>Ellipse (klein)</td>
                <td>Grün</td>
                <td>Endpunkt des Prozesses</td>
              </tr>
              <tr>
                <td>Daten</td>
                <td>Kreis (groß)</td>
                <td>Hellblau</td>
                <td>Datenspeicher und -quellen</td>
              </tr>
              <tr>
                <td>Akteur</td>
                <td>Kreis (groß)</td>
                <td>Gelb</td>
                <td>Beteiligte Personen/Organisationen</td>
              </tr>
              <tr>
                <td>Anlage</td>
                <td>Ellipse (klein)</td>
                <td>Ocker</td>
                <td>Technische Anlagen</td>
              </tr>
              <tr>
                <td>Gesetz(e)</td>
                <td>Ellipse (klein)</td>
                <td>Lila</td>
                <td>Rechtliche Grundlagen</td>
              </tr>
              <tr>
                <td>Kommentar</td>
                <td>Rechteck (groß)</td>
                <td>Weiß</td>
                <td>Anmerkungen und Notizen</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h4>2.2 Navigation, Zoom und Mehrfachselektion</h4>
        <ul>
          <li>
            <strong>Mehrfachselektion:</strong> Im Edit Mode mit gedrückter
            linker Maustaste auf freier Fläche ein Auswahlrechteck aufziehen;
            mit Strg- bzw. Shift-Klick lassen sich einzelne Knoten und
            Verbindungen zur Auswahl hinzufügen oder wieder entfernen.
            <code>Entf</code> oder der rote Löschen-Button in der
            Werkzeugleiste entfernt alle ausgewählten Elemente.
          </li>
          <li>
            <strong>Zoom:</strong> Mausrad oder die Buttons in der Werkzeugleiste.
            „🎯 Fit“ passt den Ausschnitt auf den gesamten Graphen an.
          </li>
          <li>
            <strong>Hierarchischer Zoom:</strong> Bei „100%“ werden alle
            Elemente angezeigt, bei „50%“ eine reduzierte Übersicht mit
            intelligenter Element-Sichtbarkeit.
          </li>
          <li>
            <strong>Undo/Redo:</strong> Über die Pfeil-Buttons oder
            <code>Strg+Z</code> / <code>Strg+Y</code> (10-Stufen-Historie).
          </li>
        </ul>

        <h4>2.3 Eigenschaften und Markdown-Beschreibungen</h4>
        <p>
          Jeder Knoten und jede Verbindung kann im Eigenschaften-Panel rechts
          detailliert beschrieben werden. Beschreibungen unterstützen
          Markdown-Formatierung (Überschriften, Listen, Links); im View Mode
          werden sie formatiert dargestellt. Dateien können per Drag &amp; Drop
          in den Beschreibungs-Editor gezogen werden, ein Link wird dabei
          automatisch erzeugt. Zusätzlich lassen sich erfassen:
        </p>
        <ul>
          <li><strong>Start-Datum</strong> für Start-Knoten (Zeitplanung),</li>
          <li><strong>Dauer in Wochen</strong> für Verbindungen,</li>
          <li><strong>Kosten (€)</strong> für Verbindungen.</li>
        </ul>

        <h4>2.4 Speichern, Import und Export</h4>
        <ul>
          <li>
            <strong>Auto-Save:</strong> Der Graph wird alle 30 Sekunden
            automatisch im Browser (LocalStorage) gespeichert und beim nächsten
            Öffnen wiederhergestellt.
          </li>
          <li>
            <strong>Export:</strong> „💾 Export“ speichert den Graphen als
            JSON-Datei — geeignet zum Archivieren und Weitergeben.
          </li>
          <li>
            <strong>Import:</strong> „📂 Import“ lädt eine zuvor exportierte
            JSON-Datei. Achtung: Der aktuelle Graph wird dabei ersetzt.
          </li>
        </ul>

        <h3>3. View Mode: Graphen präsentieren</h3>
        <p>
          Im View Mode 🔒 ist der Graph schreibgeschützt: Knoten lassen sich
          nicht verschieben oder bearbeiten, das Vorlagen-Panel ist
          ausgeblendet. Ein Klick auf ein Element zeigt dessen Eigenschaften
          und die formatierte Markdown-Beschreibung an — ideal, um eine fertige
          Datenlandkarte mit anderen Beteiligten durchzugehen.
        </p>
      </section>

      <PageFooter />
    </main>
  </div>
</template>

<script>
import PageFooter from './PageFooter.vue'
import '../styles/infopage.css'

export default {
  name: 'QuickstartPage',
  components: {
    PageFooter
  }
}
</script>
